import React, { useRef, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { Item } from "@thenounproject/lingo-core";
import { useCanvasDragDrop } from "./CanvasDrag/useCanvasDragDrop";

const containerStyles = css`
  /* Active drag state */
  &[data-is-dragging="true"] {
    /* Drop indicator between items */
    .drop-indicator-between {
      position: absolute;
      width: 4px;
      background: #3b82f6;
      border-radius: 2px;
      pointer-events: none;
      z-index: 20;
      transition: opacity 0.15s ease;
      opacity: 0;
      &[data-visible="true"] {
        opacity: 1;
      }
    }

    /* Items become drop zones when dragging */
    [data-droppable-item] {
      position: relative;
      cursor: copy;
    }
  }

  /* When not dragging */
  &[data-is-dragging="false"] {
    .drop-indicator-between {
      display: none;
    }
  }
`;

const DropZoneWrapper = styled.div`
  position: relative;
  margin-top: -100px;
  padding-top: 100px;
  min-height: 60vh;
  padding-bottom: 100px;
  ${containerStyles};
`;

const ItemsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

export type DropInfo = {
  type: "between-items";
  insertIndex: number;
  displayOrder: string;
  guidePosition?: "left" | "right" | "full";
  dragPosition?: "top" | "bottom" | "left" | "right";
  targetItem?: Item;
  position?: "before" | "after";
};

export type DropTarget = {
  type: "item";
  element: HTMLElement;
  index?: number;
  position?: "before" | "after";
  cursorX?: number;
  cursorY?: number;
};

export type GalleryItemsGridProps = {
  items: Item[];
  canEditContent: boolean;
  renderItem?: (item: Item, index: number) => React.ReactNode;
  onDrop?: (e: React.DragEvent, dropInfo: DropInfo) => void;
  isDragging?: boolean;
};

const GalleryItemsLayoutComponent: React.FC<GalleryItemsGridProps> = ({
  items,
  canEditContent,
  renderItem,
  onDrop,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounterRef = useRef(0);

  const { handleDragEnter, handleDragLeave, handleDragOver, handleDrop } = useCanvasDragDrop({
    container: containerRef.current,
    onDrop,
    items,
  });

  useEffect(() => {
    if (!canEditContent || !containerRef.current) return;

    const container = containerRef.current;

    const onDragEnter = (e: DragEvent) => {
      dragCounterRef.current++;
      if (dragCounterRef.current === 1) {
        setIsDragging(true);
      }
      handleDragEnter(e);
    };

    const onDragLeave = (e: DragEvent) => {
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
      }
      handleDragLeave(e);
    };

    const onDrop = (e: DragEvent) => {
      dragCounterRef.current = 0;
      setIsDragging(false);
      handleDrop(e);
    };

    const onDragEnd = () => {
      dragCounterRef.current = 0;
      setIsDragging(false);
    };

    container.addEventListener("dragenter", onDragEnter);
    container.addEventListener("dragleave", onDragLeave);
    container.addEventListener("dragover", handleDragOver);
    container.addEventListener("drop", onDrop);
    container.addEventListener("dragend", onDragEnd);

    return () => {
      container.removeEventListener("dragenter", onDragEnter);
      container.removeEventListener("dragleave", onDragLeave);
      container.removeEventListener("dragover", handleDragOver);
      container.removeEventListener("drop", onDrop);
      container.removeEventListener("dragend", onDragEnd);
    };
  }, [canEditContent, handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  return (
    <DropZoneWrapper ref={containerRef} data-is-dragging={isDragging} data-inspector-clear="true">
      <ItemsContainer data-inspector-clear="true">
        {items.map((item, index) => (
          <React.Fragment key={item.id}>{renderItem(item, index)}</React.Fragment>
        ))}
      </ItemsContainer>
    </DropZoneWrapper>
  );
};

export const GalleryItemsLayout = React.memo(GalleryItemsLayoutComponent);
GalleryItemsLayout.displayName = "GalleryItemsLayout";
